import { useMediaQuery } from "@/hooks/use-media-query";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { HGIntervalType } from "./types";

export function useBreakpoint() {
  return {
    isSm: useMediaQuery("(min-width: 640px)"),
    isMd: useMediaQuery("(min-width: 768px)"),
    isLg: useMediaQuery("(min-width: 1024px)"),
    isXl: useMediaQuery("(min-width: 1280px)"),
    is2Xl: useMediaQuery("(min-width: 1536px)"),
  };
}

// Tracks the content-box width of an element. Unlike useBreakpoint (which reads
// the viewport), this follows the element itself, so it stays correct when the
// element lives in a split pane that is only a fraction of the window.
export function useElementWidth<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(Infinity);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      setWidth(entries[0].contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, width] as const;
}

// getNow() is a module global refreshed by a timer in main.tsx, so nothing
// re-renders on its own when the clock moves on. Tick at a rate matched to the
// level on screen: seconds move every second, anything coarser can wait.
export function useNowTick(it: HGIntervalType) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const period = it === "s" ? 1000 : 30000;
    const id = setInterval(() => setTick((tick) => tick + 1), period);
    return () => clearInterval(id);
  }, [it]);
}

/**
 * `value`, but only after it has held still for `delay` ms.
 *
 * For putting a cheap control (a text input) in front of an expensive
 * derivation, so the control stays responsive and the derivation runs once per
 * pause rather than once per keystroke.
 */
export function useDebouncedValue<T>(value: T, delay: number): T {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    if (Object.is(settled, value)) return;
    const id = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(id);
    // `settled` is read to skip a no-op timer, not to re-arm one when it
    // catches up -- listing it as a dependency would do exactly that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, delay]);

  return settled;
}
